"use client";
import React, { useEffect, useMemo, useState } from "react";
import { Drawer, Button, Empty } from "antd";
import { X, Trash2, ShoppingBag } from "lucide-react";
import { useCartStore } from "context/cartStore";
import base from "lib/base";
import {
  calculateDeliveryFeeAmount,
  getDeliveryFeeDisplayValue,
  getDeliveryInfo,
} from "lib/delivery-info";
import { calculateDeliveryPricing } from "lib/quantity-discount";
import styles from "styles/components/cart-drawer.module.css";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useAuthUser } from "hooks/useAuth";
import { toast } from "sonner";

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, removeItem, updateQty } = useCartStore();
  const router = useRouter();
  const user = useAuthUser();
  const [deliveryInfo, setDeliveryInfo] = useState(null);

  const cartSummary = useMemo(() => {
    return items.reduce(
      (summary, item) => {
        const book = item.book;
        const price = book?.price?.delivery?.price || 0;
        const discount = book?.price?.delivery?.discount || 0;
        const quantityDiscountRules =
          book?.price?.delivery?.quantityDiscountRules || [];
        const pricing = calculateDeliveryPricing({
          price,
          discount,
          quantity: item.qty,
          quantityDiscountRules,
        });
        const subtotal = price * (item.qty || 1);

        return {
          subtotal: summary.subtotal + subtotal,
          discount: summary.discount + Math.max(0, subtotal - pricing.finalTotal),
          total: summary.total + pricing.finalTotal,
        };
      },
      { subtotal: 0, discount: 0, total: 0 },
    );
  }, [items]);
  const deliveryFee = calculateDeliveryFeeAmount(deliveryInfo, cartSummary.total);
  const deliveryFeeText = getDeliveryFeeDisplayValue(
    deliveryInfo,
    cartSummary.total,
  );
  const payableTotal = cartSummary.total + deliveryFee;

  useEffect(() => {
    getDeliveryInfo().then(setDeliveryInfo);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleRemove = (id) => {
    removeItem(id, user);
  };

  const handleQtyChange = (id, val, maxStock) => {
    const next = Math.min(maxStock || 99, Math.max(1, Number(val || 1)));
    updateQty(id, next, user);
  };

  const handleQtyStep = (id, nextQty, maxStock) => {
    const next = Math.min(maxStock || 99, Math.max(1, Number(nextQty || 1)));
    updateQty(id, next, user);
  };

  const handleCheckout = () => {
    // Validate stock for all items
    const outOfStockItems = items.filter((item) => {
      const book = item.book;
      const stockQty = book?.price?.delivery?.quantity || 0;
      return item.qty > stockQty;
    });

    if (outOfStockItems.length > 0) {
      const itemNames = outOfStockItems
        .map((item) => item.book.name)
        .join(", ");
      toast.error(`Барааны үлдэгдэл хүрэлцэхгүй байна: ${itemNames}`, {
        duration: 4000,
      });
      return;
    }

    handleClose();
    router.push("/checkout");
  };

  const isObjectId = (value) =>
    typeof value === "string" && /^[a-f\d]{24}$/i.test(value);

  const resolveImage = (book) => {
    const raw =
      book?.pictures?.[0] || book?.images?.[0] || book?.cover || book?.image;
    if (!raw) return "/assets/img/no-photo.png"; // fallback
    if (/^https?:\/\//i.test(raw)) return raw;

    const cdnBase = base.cdnUrl.endsWith("/") ? base.cdnUrl : `${base.cdnUrl}/`;
    let cleaned = String(raw).replace(/^\/+/, "");
    if (cleaned.startsWith("uploads/") && /\/uploads\/$/i.test(cdnBase)) {
      cleaned = cleaned.replace(/^uploads\//, "");
    }
    return `${cdnBase}${cleaned}`;
  };

  return (
    <Drawer
      title="Миний сагс"
      placement="right"
      onClose={handleClose}
      open={isOpen}
      width={460}
      className={styles.cartDrawer}
      styles={{ body: { padding: 0 } }}
    >
      <div className={styles.container}>
        {items.length === 0 ? (
          <div className={styles.emptyState}>
            <Empty description="Сагс хоосон байна" />
            <Button type="primary" onClick={handleClose} className="mt-4">
              Худалдан авалт хийх
            </Button>
          </div>
        ) : (
          <>
            <div className={styles.itemsList}>
              {items.map((item) => {
                // item.book might be an ID if not populated correctly,
                // but our store logic tries to ensure we have object.
                // However, coming from server sync, it IS populated.
                // Coming from local `addToCart`, passed book is object.
                const book = item.book;
                if (!book) return null;

                const stockQty = book?.price?.delivery?.quantity || 0;
                const price = book?.price?.delivery?.price || 0;
                const discount = book?.price?.delivery?.discount || 0;
                const quantityDiscountRules =
                  book?.price?.delivery?.quantityDiscountRules || [];
                const pricing = calculateDeliveryPricing({
                  price,
                  discount,
                  quantity: item.qty,
                  quantityDiscountRules,
                });
                const finalPrice = pricing.finalUnitPrice;
                const bookSlug = book?.slug ? `/books/${book.slug}` : "/books";

                const authorName =
                  (Array.isArray(book.authors) && book.authors[0]
                    ? book.authors[0].nickname || book.authors[0].name
                    : null) ||
                  (typeof book.author === "object"
                    ? book.author?.name || book.author?.title
                    : isObjectId(book.author)
                      ? ""
                      : book.author) ||
                  "";

                return (
                  <div key={book._id} className={styles.cartItem}>
                    <div className={styles.itemImage}>
                      <img src={resolveImage(book)} alt={book.name} />
                    </div>
                    <div className={styles.itemDetails}>
                      <div className={styles.itemHeader}>
                        <div className={styles.itemTitleBlock}>
                          <h4 className={styles.itemName}>
                            <Link href={bookSlug} onClick={handleClose}>
                              {book.name}
                            </Link>
                          </h4>
                          {authorName && (
                            <p className={styles.itemAuthor}>{authorName}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={() => handleRemove(book._id)}
                          aria-label="Сагсаас устгах"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>

                      <div className={styles.pricePanel}>
                        <div className={styles.unitPriceGroup}>
                          <span className={styles.priceLabel}>Нэгж үнэ</span>
                          <div className={styles.priceRow}>
                            <span className={styles.price}>
                              {finalPrice.toLocaleString()}₮
                            </span>
                            {(discount > 0 ||
                              pricing.quantityDiscountTotal > 0) && (
                              <span className={styles.oldPrice}>
                                {price.toLocaleString()}₮
                              </span>
                            )}
                          </div>
                        </div>
                        {pricing.quantityDiscountPercent > 0 && (
                          <div className={styles.discountHint}>
                            {pricing.appliedRule?.minQuantity}ш+ /{" "}
                            {pricing.quantityDiscountPercent}% хямдрал
                          </div>
                        )}
                      </div>

                      <div className={styles.itemFooter}>
                        <div className={styles.qtyArea}>
                          <span className={styles.qtyLabel}>Тоо ширхэг</span>
                          <div className={styles.qtyControl}>
                            <button
                              type="button"
                              className={`${styles.qtyBtn} ${styles.qtyBtnMinus}`}
                              onClick={() =>
                                handleQtyStep(book._id, item.qty - 1, stockQty)
                              }
                              disabled={item.qty <= 1}
                              aria-label="Decrease quantity"
                            >
                              -
                            </button>
                            <span className={styles.qtyValue}>{item.qty}</span>
                            <button
                              type="button"
                              className={`${styles.qtyBtn} ${styles.qtyBtnPlus}`}
                              onClick={() =>
                                handleQtyStep(book._id, item.qty + 1, stockQty)
                              }
                              disabled={item.qty >= stockQty || item.qty >= 99}
                              aria-label="Increase quantity"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.footer}>
              <div className={styles.summaryBox}>
                <div className={styles.summaryLine}>
                  <span>Нийт дүн</span>
                  <span>{cartSummary.subtotal.toLocaleString()}₮</span>
                </div>
                {cartSummary.discount > 0 && (
                  <div className={styles.summaryLine}>
                    <span>Хөнгөлөлт</span>
                    <span className={styles.summaryDiscount}>
                      -{cartSummary.discount.toLocaleString()}₮
                    </span>
                  </div>
                )}
                <div className={styles.summaryLine}>
                  <span>Хүргэлт</span>
                  <span>{deliveryFeeText}</span>
                </div>
                <div className={styles.totalRow}>
                  <span>Төлөх дүн</span>
                  <span className={styles.totalAmount}>
                    {payableTotal.toLocaleString()}₮
                  </span>
                </div>
              </div>
              <Button
                type="primary"
                block
                size="middle"
                className={styles.checkoutBtn}
                onClick={handleCheckout}
              >
                Захиалах
              </Button>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default CartDrawer;
